<template>
  <global-dialog-card v-model="model" :title="$t('import_csv')" :max-width="900">
    <v-container>
      <v-row>
        <v-col cols="12">
          <!-- File Upload Section -->
          <v-card outlined class="mb-4">
            <v-card-text>
              <v-row align="center">
                <v-col cols="12" md="8">
                  <v-file-input
                    v-model="selectedFile"
                    :label="$t('select_csv_file')"
                    accept=".csv"
                    prepend-icon="mdi-file-delimited"
                    dense
                    solo
                    flat
                    class="custom-field"
                    show-size
                    @change="handleFileChange"
                    :hint="$t('csv_format_hint')"
                    persistent-hint
                    :disabled="isImporting"
                  ></v-file-input>
                </v-col>
                <v-col cols="12" md="4" class="text-right">
                  <v-btn
                    text
                    color="primary"
                    @click="downloadTemplate"
                    :disabled="isImporting"
                  >
                    <v-icon left>mdi-download</v-icon>
                    {{ $t("download_template") }}
                  </v-btn>
                </v-col>
              </v-row>

              <!-- Summary Stats -->
              <v-row v-if="items.length > 0" class="mt-2">
                <v-col cols="12">
                  <v-chip class="mr-2" color="primary" outlined>
                    <v-icon left small>mdi-account-multiple</v-icon>
                    {{ $t("total") }}: {{ items.length }}
                  </v-chip>
                  <v-chip class="mr-2" color="success" outlined>
                    <v-icon left small>mdi-check-circle</v-icon>
                    {{ $t("csv_ready") }}: {{ readyCount }}
                  </v-chip>
                  <v-chip class="mr-2" color="warning" outlined>
                    <v-icon left small>mdi-alert-circle</v-icon>
                    {{ $t("csv_not_ready") }}: {{ notReadyCount }}
                  </v-chip>
                  <v-chip v-if="importedCount > 0" class="mr-2" color="info" outlined>
                    <v-icon left small>mdi-upload</v-icon>
                    {{ $t("imported") }}: {{ importedCount }}
                  </v-chip>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Data Table -->
          <v-card outlined v-if="items.length > 0">
            <v-card-text class="pa-0">
              <v-data-table
                :headers="tableHeaders"
                :items="items"
                :items-per-page="10"
                class="striped-table"
                :loading="isImporting"
                :loading-text="$t('importing_users')"
              >
                <template v-slot:item.index="{ item, index }">
                  {{ index + 1 }}
                </template>
                <template v-slot:item.name="{ item }">
                  {{ toLatin(item.name) || "-" }}
                </template>
                <template v-slot:item.lastname="{ item }">
                  {{ toLatin(item.lastname) || "-" }}
                </template>
                <template v-slot:item.email="{ item }">
                  {{ item.email || "-" }}
                </template>
                <template v-slot:item.info="{ item }">
                  <v-chip
                    x-small
                    :color="item.state ? 'success' : 'error'"
                    :outlined="!item.state"
                  >
                    <v-icon left x-small>
                      {{ item.state ? "mdi-check-circle" : "mdi-alert-circle" }}
                    </v-icon>
                    {{ item.state ? $t("valid") : $t("invalid") }}
                  </v-chip>
                </template>
                <template v-slot:item.statusImport="{ item }">
                  <v-chip
                    x-small
                    :color="getStatusColor(item.statusImport)"
                    :outlined="item.statusImport === 'ready'"
                  >
                    <v-icon left x-small>
                      {{ getStatusIcon(item.statusImport) }}
                    </v-icon>
                    {{ $t("csv_" + item.statusImport.replace(" ", "_")) }}
                  </v-chip>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>

          <!-- Empty State -->
          <v-card outlined v-else>
            <v-card-text>
              <empty-state
                icon="mdi-file-upload-outline"
                :title="$t('no_file_selected')"
                :subtitle="$t('select_csv_file_to_import')"
              />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <template #actions>
      <v-btn text @click="close" :disabled="isImporting">
        {{ $t("cancel") }}
      </v-btn>
      <primary-button
        @click="importUsers"
        :disabled="!canImport || isImporting"
        :loading="isImporting"
        :dark="!(!canImport || isImporting)"
      >
        <v-icon left>mdi-upload</v-icon>
        {{ $t("import_users") }}
      </primary-button>
    </template>
  </global-dialog-card>
</template>

<script>
import Papa from "papaparse";
import hasModel from "@/mixins/hasModel";
import hasSnackbar from "@/mixins/hasSnackbar";
import importService from "@/services/import";
import EmptyState from "@/components/global/EmptyState.vue";

export default {
  props: {
    default: false,
  },
  mixins: [hasModel, hasSnackbar],
  components: {
    EmptyState,
  },
  data() {
    return {
      items: [],
      selectedFile: null,
      isImporting: false,
      schema: {
        "First Name": {
          prop: "name",
          type: String,
          required: true,
        },
        "Last Name": {
          prop: "lastname",
          type: String,
          required: true,
        },
        Email: {
          prop: "email",
          type: String,
          required: true,
        },
      },
    };
  },
  computed: {
    tableHeaders() {
      return [
        { text: "#", value: "index", width: 60 },
        { text: this.$t("name"), value: "name" },
        { text: this.$t("last_name"), value: "lastname" },
        { text: this.$t("email"), value: "email" },
        { text: this.$t("data_validation"), value: "info", width: 120 },
        { text: this.$t("status"), value: "statusImport", width: 150 },
      ];
    },
    readyCount() {
      return this.items.filter((item) => item.state && item.statusImport === "ready")
        .length;
    },
    notReadyCount() {
      return this.items.filter((item) => !item.state || item.statusImport === "not ready")
        .length;
    },
    importedCount() {
      return this.items.filter((item) => item.statusImport === "success").length;
    },
    canImport() {
      return this.items.length > 0 && this.readyCount > 0;
    },
  },
  methods: {
    close() {
      this.model = false;
      this.items = [];
      this.selectedFile = null;
    },
    getStatusColor(status) {
      switch (status) {
        case "ready":
          return "info";
        case "not ready":
          return "warning";
        case "success":
          return "success";
        case "failed":
          return "error";
        default:
          return "grey";
      }
    },
    getStatusIcon(status) {
      switch (status) {
        case "ready":
          return "mdi-check-circle-outline";
        case "not ready":
          return "mdi-alert-circle-outline";
        case "success":
          return "mdi-check-circle";
        case "failed":
          return "mdi-close-circle";
        default:
          return "mdi-help-circle-outline";
      }
    },
    async importUsers() {
      if (!this.canImport) return;

      this.isImporting = true;
      const chunkSize = 10;
      let successCount = 0;
      let failedCount = 0;

      try {
        for (let i = 0; i < this.items.length; i += chunkSize) {
          let chunk = this.items.slice(i, i + chunkSize);
          chunk = chunk.filter((item) => item.state);

          if (chunk.length === 0) continue;

          await importService
            .importUsersChunk(chunk, this.$route.params.id)
            .then((res) => {
              res.data.data.forEach((data) => {
                const foundItems = this.items.filter((item) => item.email === data.email);
                foundItems.forEach((item) => {
                  item.statusImport = data.state;
                  if (data.state === "success") {
                    successCount++;
                  } else if (data.state === "failed") {
                    failedCount++;
                  }
                });
              });
            })
            .catch((error) => {
              chunk.forEach((item) => {
                item.statusImport = "failed";
                failedCount++;
              });
            });
        }

        // Show summary message
        if (successCount > 0) {
          this.showSuccessSnackbar(
            this.$t("users_imported_successfully").replace("{count}", successCount)
          );
        }
        if (failedCount > 0) {
          this.showErrorSnackbar(
            this.$t("users_import_failed").replace("{count}", failedCount)
          );
        }

        // Emit event to parent component to refresh user list
        if (successCount > 0) {
          this.$emit("import-completed");
        }
      } catch (error) {
        this.showErrorSnackbar(this.$t("import_error"));
        console.error("Import error:", error);
      } finally {
        this.isImporting = false;
      }
    },
    handleFileChange(file) {
      if (!file) {
        this.items = [];
        return;
      }

      if (!file.name.endsWith(".csv")) {
        this.showErrorSnackbar(this.$t("invalid_file_format"));
        this.selectedFile = null;
        return;
      }

      this.parseCSV(file);
    },
    parseCSV(file) {
      const arr = [];

      Papa.parse(file, {
        complete: (results) => {
          if (results.data.length === 0) {
            this.showErrorSnackbar(this.$t("empty_csv_file"));
            return;
          }

          results.data.forEach((row, index) => {
            // Skip header row
            if (index === 0) return;

            // Skip empty rows
            if (!row[0] && !row[1] && !row[2]) return;

            const isValid = this.validateRow(row);
            const user = {
              name: row[0]?.trim() || "",
              lastname: row[1]?.trim() || "",
              email: row[2]?.trim() || "",
              state: isValid,
              statusImport: isValid ? "ready" : "not ready",
            };

            arr.push(user);
          });

          this.items = arr;

          if (arr.length === 0) {
            this.showErrorSnackbar(this.$t("no_valid_data_found"));
          } else {
            this.showSuccessSnackbar(
              this.$t("csv_parsed_successfully").replace("{count}", arr.length)
            );
          }
        },
        error: (error) => {
          this.showErrorSnackbar(this.$t("csv_parse_error"));
          console.error("CSV parse error:", error);
        },
      });
    },
    validateRow(row) {
      const name = row[0]?.trim();
      const lastname = row[1]?.trim();
      const email = row[2]?.trim();

      if (!name || !lastname || !email) {
        return false;
      }

      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        return false;
      }

      return true;
    },
    downloadTemplate() {
      const csvContent = "First Name,Last Name,Email\n";
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute("download", "import-template.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      this.showSuccessSnackbar(this.$t("template_downloaded"));
    },
  },
};
</script>
